import React from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";
import { NervousLoop } from "@/components/site/NervousLoop";

const IMG = {
  hero: "https://customer-assets-v7afamib.emergentagent.net/job_movement-restored-5/artifacts/ip1k0dnm_IMG_2552.jpeg",
  soft: "https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  walk: "https://images.unsplash.com/photo-1556775932-b5cc4f1e1d1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

const MODALITIES = [
  ["Chiropractic Care", "Adjusting a restricted joint changes the stream of information (afferent input) traveling from that joint into the spinal cord and brain. Research suggests this can influence how the central nervous system senses and controls the area — affecting muscle tone, joint position sense and movement coordination."],
  ["Myofascial & Soft-Tissue Care", "Muscle and fascia are dense with mechanoreceptors. Skilled soft-tissue work alters that sensory feedback, which studies associate with reduced protective muscle guarding and improved tissue tolerance — a nervous-system response as much as a mechanical one."],
  ["Functional Movement", "Movement is the language of the nervous system. Loading a joint through good motion refines the sensory-motor 'map' the brain uses to control your body, helping restore confident, coordinated movement."],
  ["Dry Needling & Cupping", "By changing the local sensory environment, these techniques can influence muscle tone and the pain-modulation systems the nervous system uses to turn discomfort up or down."],
  ["SoftWave & Laser", "These modalities introduce a controlled stimulus at the tissue level that the body responds to — part of the same feedback loop between tissue, sensation and central processing."],
  ["Craniosacral & Gentle Care", "Very light-touch approaches work with the calming, restorative side of the autonomic nervous system — the 'rest, digest and recover' state where the body heals best."],
];

export default function NervousSystem() {
  return (
    <div>
      <Seo title="Nervous System Focused Chiropractic | Psalm 30:2 Gardner KS" description="Every modality we use — chiropractic, myofascial and functional care — works through your nervous system. Learn the science of top-down and bottom-up control, posture and pain-free movement." />
      <PageHero
        eyebrow="The Nervous System"
        title={<>Everything we do<br />works through your<br /><span className="italic text-taupe">nervous system.</span></>}
        intro="We're a nervous-system-focused practice. Your brain and spinal cord are the master control system for movement, posture, healing and how you experience pain. Every modality we use is a way of communicating with it."
        image={IMG.hero}
      />

      {/* Master system */}
      <section className="bg-ivory py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-5">
            <div className="overline mb-6">Why it matters</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] text-espresso">Your body's<br />master system.</h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 space-y-6 text-lg font-light leading-relaxed text-espressosoft/85">
            <p>Every heartbeat, breath, step and posture is coordinated by your nervous system. It constantly gathers information from your joints, muscles and fascia, processes it in the brain and spinal cord, and sends instructions back out to move and stabilize you.</p>
            <p>When a joint stops moving well or tissue becomes restricted, the <em>quality of that information</em> changes. The nervous system adapts — often by guarding, tightening or altering how you move. Over time, that adaptation can show up as stiffness, recurring tension or pain.</p>
            <p>Our job isn't to "put a bone back." It's to change the input, so the nervous system can update its control of the area and let you move freely again.</p>
          </Reveal>
        </div>
      </section>

      <NervousLoop />

      {/* Every modality */}
      <section className="bg-linen py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="overline mb-5">One target, many tools</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso max-w-2xl">Every modality is a conversation with the nervous system.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 mt-14">
            {MODALITIES.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 2) * 80}>
                <div className="border-t border-espresso/15 pt-6">
                  <h3 className="font-serif text-2xl text-espresso mb-3">{t}</h3>
                  <p className="text-espressosoft/80 font-light leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Top-down / bottom-up */}
      <section className="bg-espresso text-ivory py-24 lg:py-36 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="overline !text-brass mb-6">The science of control</div>
            <h2 className="font-serif font-light text-4xl lg:text-6xl leading-[1.05] max-w-3xl">Top-down. Bottom-up. One loop.</h2>
            <p className="mt-6 text-ivory/70 font-light text-lg max-w-2xl">Your nervous system controls the body in two directions at once. Healthy movement and posture depend on both working together.</p>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-8 mt-16">
            <Reveal>
              <div className="bg-ivory/[0.06] border border-ivory/10 rounded-sm p-8 lg:p-10 h-full">
                <ArrowDown className="text-brass mb-5" strokeWidth={1.4} />
                <h3 className="font-serif text-3xl mb-4">Top-down</h3>
                <p className="text-ivory/75 font-light leading-relaxed mb-4">The brain sends instructions <em>down</em> to the body — setting muscle tone, coordinating posture, and using descending pathways to turn pain signals up or down. Stress, focus and past experience all shape these commands.</p>
                <p className="text-ivory/75 font-light leading-relaxed">Calming the system and improving central control (descending inhibition) is one way manual care and movement are thought to reduce pain and guarding.</p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="bg-ivory/[0.06] border border-ivory/10 rounded-sm p-8 lg:p-10 h-full">
                <ArrowUp className="text-brass mb-5" strokeWidth={1.4} />
                <h3 className="font-serif text-3xl mb-4">Bottom-up</h3>
                <p className="text-ivory/75 font-light leading-relaxed mb-4">Sensors in your feet, joints, muscles and fascia send information <em>up</em> to the brain — a constant map of where you are and how you're loaded. This is why a restricted ankle or hip can quietly change control far away.</p>
                <p className="text-ivory/75 font-light leading-relaxed">Adjustments, soft-tissue work and loaded movement change this input, giving the brain a clearer, more accurate map to work from.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Kinetic chain → posture → pain-free */}
      <section className="bg-ivory py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <div className="overline mb-6">Chain · Posture · Motion</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] text-espresso mb-8">More motion and better posture keep the body pain-free.</h2>
            <div className="space-y-5 text-lg font-light leading-relaxed text-espressosoft/85">
              <p>The kinetic chain — foot to head — feeds the nervous system with information at every link. When each link moves well, the brain receives a rich, accurate signal and can hold you in efficient, upright posture with less effort.</p>
              <p>Research links improved joint mobility and postural control with better proprioception and reduced pain sensitivity. In practical terms: <span className="text-espresso">restore motion → sharpen the nervous system's map → improve posture → distribute load evenly → stay comfortable.</span></p>
              <p>That's why we assess the whole chain, not just the sore spot — and why we always reassess and then load. Movement is what makes the change stick.</p>
            </div>
            <Link to="/approach" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso link-underline">See how we assess the chain <ArrowRight size={15} /></Link>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="overflow-hidden rounded-sm aspect-[4/5] lg:ml-auto lg:max-w-md">
              <img src={IMG.walk} alt="Confident, pain-free movement" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Honest science / disclaimer + CTA */}
      <section className="bg-linen py-20 lg:py-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <div className="overline mb-5">The science, honestly</div>
            <p className="font-serif font-light text-2xl lg:text-3xl leading-snug text-espresso mb-6">We don't claim to cure disease. We support how your nervous system senses, moves and recovers — and let your body do what it's designed to do.</p>
            <p className="text-sm text-taupe max-w-xl mx-auto mb-10">This page is educational and reflects current understanding of neuroscience and manual care. It is not medical advice, a diagnosis, or a guarantee of results. Individual experiences vary and care is always tailored to you.</p>
            <BookButton label="Book Your Visit" variant="primary" track_label="nervous-system" testid="nervous-book-button" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
